import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceCategory from "@/pages/ServiceCategory";
import ServiceDetail from "@/pages/ServiceDetail";
import SosPrint from "@/pages/SosPrint";
import Portfolio from "@/pages/Portfolio";
import Tarifs from "@/pages/Tarifs";
import About from "@/pages/About";
import ChooseHelp from "@/pages/ChooseHelp";
import CreerMonSupport from "@/pages/CreerMonSupport";
import PreparerMonProjet from "@/pages/PreparerMonProjet";
import PlancheConfigurator from "@/pages/PlancheConfigurator";
import FormatConfigurator from "@/pages/FormatConfigurator";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import OrderDetail from "@/pages/OrderDetail";
import PaymentSuccess from "@/pages/PaymentSuccess";
import BillingInfo from "@/pages/BillingInfo";
import MyInvoices from "@/pages/MyInvoices";
import MyClients from "@/pages/MyClients";
import PayLink from "@/pages/PayLink";
import PaymentCancel from "@/pages/PaymentCancel";
import OrderWizard from "@/pages/OrderWizard";
import QuoteRequest from "@/pages/QuoteRequest";
import Admin from "@/pages/Admin";
import ContactStudio from "@/pages/ContactStudio";
import Profile from "@/pages/Profile";
import CreationConfigurator from "@/pages/CreationConfigurator";
import ComplianceConfigurator from "@/pages/ComplianceConfigurator";
import EditionConfigurator from "@/pages/EditionConfigurator";
import PrepresseConfigurator from "@/pages/PrepresseConfigurator";
import CouvertureConfigurator from "@/pages/CouvertureConfigurator";
import CouvertureCompleteConfigurator from "@/pages/CouvertureCompleteConfigurator";
import AdapterCouvertureConfigurator from "@/pages/AdapterCouvertureConfigurator";
import ImpressionLivraison from "@/pages/ImpressionLivraison";
import MentionsLegales from "@/pages/MentionsLegales";
import CGV from "@/pages/CGV";
import Confidentialite from "@/pages/Confidentialite";
import "@/App.css";

function Protected({ children, admin }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-sm font-mono">Chargement…</div>;
  if (!user) return <Navigate to={`/connexion?redirect=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/espace" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/creer-mon-support" element={<CreerMonSupport />} />
              <Route path="/preparer-mon-projet" element={<PreparerMonProjet />} />
              <Route path="/services/:category" element={<ServiceCategory />} />
              <Route path="/prestation/:slug" element={<ServiceDetail />} />
              <Route path="/sos-print" element={<SosPrint />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/tarifs" element={<Tarifs />} />
              <Route path="/a-propos" element={<About />} />
              <Route path="/aide-choix" element={<ChooseHelp />} />
              <Route path="/connexion" element={<Login />} />
              <Route path="/inscription" element={<Register />} />
              <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
              <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
              {/* Anciens formulaires /commander/:slug remplacés par les nouvelles interfaces dédiées */}
              <Route path="/commander/fichier-conforme" element={<Navigate to="/configurer/conforme" replace />} />
              <Route path="/commander/planche-pdf-prete" element={<Navigate to="/configurer/planche" replace />} />
              <Route path="/commander/creation-support" element={<Navigate to="/creer-mon-support" replace />} />
              <Route path="/commander/adapter-format" element={<Navigate to="/configurer/format" replace />} />
              <Route path="/commander/controle-prepresse" element={<Navigate to="/configurer/prepresse" replace />} />
              <Route path="/commander/:slug" element={<Protected><OrderWizard /></Protected>} />
              <Route path="/configurer/planche" element={<Protected><PlancheConfigurator /></Protected>} />
              <Route path="/configurer/format" element={<Protected><FormatConfigurator /></Protected>} />
              <Route path="/configurer/creation" element={<Protected><CreationConfigurator /></Protected>} />
              <Route path="/configurer/conforme" element={<Protected><ComplianceConfigurator /></Protected>} />
              <Route path="/configurer/edition" element={<Protected><EditionConfigurator /></Protected>} />
              <Route path="/configurer/prepresse" element={<Protected><PrepresseConfigurator /></Protected>} />
              <Route path="/configurer/premiere-de-couverture" element={<Protected><CouvertureConfigurator /></Protected>} />
              <Route path="/configurer/couverture-complete" element={<Protected><CouvertureCompleteConfigurator /></Protected>} />
              <Route path="/configurer/adapter-couverture" element={<Protected><AdapterCouvertureConfigurator /></Protected>} />
              <Route path="/impression-livraison" element={<ImpressionLivraison />} />
              <Route path="/commander/premiere-de-couverture" element={<Navigate to="/configurer/premiere-de-couverture" replace />} />
              <Route path="/commander/couverture-complete" element={<Navigate to="/configurer/couverture-complete" replace />} />
              <Route path="/commander/adapter-couverture" element={<Navigate to="/configurer/adapter-couverture" replace />} />
              <Route path="/mise-en-page" element={<Protected><EditionConfigurator /></Protected>} />
              <Route path="/services/edition" element={<Navigate to="/mise-en-page" replace />} />
              <Route path="/demande-devis" element={<Protected><QuoteRequest /></Protected>} />
              <Route path="/espace" element={<Protected><Dashboard /></Protected>} />
              <Route path="/espace/profil" element={<Protected><Profile /></Protected>} />
              <Route path="/espace/facturation" element={<Protected><BillingInfo /></Protected>} />
              <Route path="/espace/factures" element={<Protected><MyInvoices /></Protected>} />
              <Route path="/espace/clients" element={<Protected><MyClients /></Protected>} />
              <Route path="/reglement/:token" element={<PayLink />} />
              <Route path="/espace/contact" element={<Protected><ContactStudio /></Protected>} />
              <Route path="/espace/commande/:id" element={<Protected><OrderDetail /></Protected>} />
              <Route path="/paiement/succes" element={<PaymentSuccess />} />
              <Route path="/paiement/annule" element={<PaymentCancel />} />
              <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/cgv" element={<CGV />} />
              <Route path="/confidentialite" element={<Confidentialite />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
